import { useState, useEffect } from 'react'
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { useFCMToken } from '@/modules/notification/hooks/useFCMToken'
import { isFCMSupported } from '@/modules/notification/utils/firebase'
import { cn } from '@/common/lib/utils'

interface NotificationPermissionButtonProps {
	className?: string
	showStatusText?: boolean
}

export function NotificationPermissionButton({
	className,
	showStatusText = true,
}: NotificationPermissionButtonProps) {
	const {
		isLoading,
		error,
		requestPermissionAndRegisterToken,
		checkPermissionStatus,
	} = useFCMToken()

	const [permission, setPermission] =
		useState<NotificationPermission>('default')
	const [isSupported, setIsSupported] = useState(true)

	useEffect(() => {
		setIsSupported(isFCMSupported())
		setPermission(checkPermissionStatus())
	}, [checkPermissionStatus])

	const handleRequestPermission = async () => {
		const token = await requestPermissionAndRegisterToken()
		if (token) {
			setPermission('granted')
		} else {
			// Re-check permission status in case it was denied
			setPermission(checkPermissionStatus())
		}
	}

	// Don't render if FCM is not supported (e.g., iOS Safari non-PWA)
	if (!isSupported) {
		return null
	}

	// Permission already granted
	if (permission === 'granted') {
		return (
			<div
				className={cn(
					'flex items-center gap-2 text-sm text-green-600',
					className,
				)}
			>
				<BellRing className="h-4 w-4" />
				{showStatusText && <span>การแจ้งเตือนเปิดอยู่</span>}
			</div>
		)
	}

	// Permission denied
	if (permission === 'denied') {
		return (
			<div
				className={cn(
					'flex items-center gap-2 text-sm text-muted-foreground',
					className,
				)}
			>
				<BellOff className="h-4 w-4" />
				{showStatusText && (
					<span>การแจ้งเตือนถูกปิด (กรุณาเปิดในการตั้งค่า)</span>
				)}
			</div>
		)
	}

	// Permission default - show button to request
	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<Button
				type="button"
				variant="outline"
				onClick={handleRequestPermission}
				disabled={isLoading}
				className="w-fit"
			>
				{isLoading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Bell className="h-4 w-4" />
				)}
				{isLoading ? 'กำลังดำเนินการ...' : 'เปิดการแจ้งเตือน'}
			</Button>
			{error && <p className="text-sm text-destructive">{error}</p>}
		</div>
	)
}
